import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import PromoTypeTag from './PromoTypeTag';

const { FiCheckCircle, FiExternalLink, FiMousePointer } = FiIcons;

const PromoCard = ({ promo, direction, onClickPromo, userId }) => {
  const [clicked, setClicked] = useState(false);
  const partner = direction === 'outgoing' ? promo.promoted : promo.promoter;
  if (!partner) return null;

  const handleClick = async () => {
    if (clicked) return;
    setClicked(true);
    await onClickPromo(promo.id, userId);
  };

  const hasImage = promo.cover_image || partner.cover_image || partner.avatar_image;

  return (
    <div className="group relative rounded-3xl border border-zinc-800 bg-zinc-900/40 hover:border-zinc-600 transition-all overflow-hidden">
      {/* Cover */}
      <div className="aspect-video relative overflow-hidden bg-zinc-900">
        {hasImage ? (
          <img
            src={promo.cover_image || partner.cover_image || partner.avatar_image}
            alt={promo.title}
            className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-7xl font-black text-zinc-800">
            {partner.name?.[0]?.toUpperCase()}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />

        {/* Type badge */}
        <div className="absolute top-4 left-4">
          <PromoTypeTag type={promo.promo_type} />
        </div>

        {/* Direction badge */}
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
            direction === 'outgoing'
              ? 'bg-zinc-900/80 border-zinc-700 text-zinc-400'
              : 'bg-white/10 border-white/20 text-white'
          }`}>
            {direction === 'outgoing' ? 'You promote' : 'Promotes you'}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-6 space-y-4">
        {/* Partner info */}
        <Link to={`/${partner.slug}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 overflow-hidden flex-shrink-0">
            {partner.avatar_image ? (
              <img src={partner.avatar_image} alt={partner.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm font-black text-zinc-500">
                {partner.name?.[0]?.toUpperCase()}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-black text-white truncate">{partner.name}</p>
              {partner.verified && <SafeIcon icon={FiCheckCircle} className="text-blue-500 text-xs flex-shrink-0" />}
            </div>
            <p className="text-zinc-600 text-[10px] font-black uppercase tracking-widest">{partner.genre || 'Artist'}</p>
          </div>
        </Link>

        {/* Title & Message */}
        <div className="space-y-2">
          <h3 className="text-lg font-black text-white uppercase tracking-tight leading-tight">{promo.title}</h3>
          {promo.message && (
            <p className="text-zinc-400 text-sm font-bold leading-relaxed line-clamp-2">{promo.message}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
          <div className="flex items-center gap-1.5 text-zinc-600 text-xs font-black uppercase tracking-widest">
            <SafeIcon icon={FiMousePointer} className="text-sm" />
            {promo.clicks || 0} clicks
          </div>

          <div className="flex items-center gap-2">
            <Link to={`/${partner.slug}`}
              className="px-4 py-2 rounded-xl border border-zinc-700 text-zinc-300 text-xs font-black uppercase tracking-widest hover:border-white hover:text-white transition-all">
              Visit
            </Link>
            {promo.link_url && (
              <a href={promo.link_url} target="_blank" rel="noreferrer"
                onClick={handleClick}
                className="px-4 py-2 rounded-xl bg-white text-black text-xs font-black uppercase tracking-widest hover:scale-105 active:scale-95 transition-all flex items-center gap-1.5">
                <SafeIcon icon={FiExternalLink} className="text-xs" /> Link
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCard;